import React from "react";

import { IPartnerMatchCard } from "../../../services/zeos-api/contracts/Favorability";

interface IProps {
  match: IPartnerMatchCard;
  onRequestIntroduction: (partnerId: number, partnerName: string) => void;
  isSubmitting?: boolean;
}

export const PartnerMatchCard = ({
  match,
  onRequestIntroduction,
  isSubmitting,
}: IProps) => {
  const isTop = match.rank === 1;
  const isPotential = match.tier.toLowerCase().includes("potential");
  const rankBg = isPotential ? "bg-[#9aa3c0]" : "bg-[#F5841F]";
  const scoreColor = isPotential ? "text-[#7c86a8]" : "text-[#F5841F]";
  const borderClass = isTop
    ? "border-2 border-[#F5841F] shadow-[0_2px_10px_rgba(245,132,31,0.12)]"
    : "border border-[#e4e4e4]";

  return (
    <div
      className={`rounded-xl px-6 py-[22px] mb-4 grid grid-cols-1 md:grid-cols-[52px_1fr_190px] gap-5 items-start ${borderClass}`}
    >
      <div
        className={`w-11 h-11 rounded-full ${rankBg} text-white font-extrabold text-[19px] flex items-center justify-center`}
      >
        {match.rank}
      </div>

      <div>
        <div className="text-[18.5px] font-bold text-[#2b2b2b] flex items-center gap-2.5 flex-wrap">
          {match.name}
          <span className="text-[11.5px] font-bold text-[#6b6b6b] bg-[#f3f3f3] rounded-md px-2 py-0.5 uppercase tracking-[0.05em]">
            {match.category}
          </span>
        </div>
        <p className="text-sm text-[#6b6b6b] mt-1.5 mb-2.5 max-w-[640px]">
          {match.oneLineSummary}
        </p>
        {match.statsLine && (
          <p className="text-[13.5px] text-[#2b2b2b] mb-3">
            {renderStats(match.statsLine)}
          </p>
        )}
        <div className="flex gap-2 flex-wrap">
          {match.reasonChips.map((chip) => {
            const isEvidence = /L[2-4]/i.test(chip);
            return (
              <span
                key={chip}
                className={
                  isEvidence
                    ? "bg-[#E9F5EE] text-[#2E8B57] text-[12.5px] font-semibold px-[11px] py-[5px] rounded-full"
                    : "bg-[#FDF1E4] text-[#B05E0B] text-[12.5px] font-semibold px-[11px] py-[5px] rounded-full"
                }
              >
                {chip}
              </span>
            );
          })}
        </div>
      </div>

      <div className="flex md:flex-col items-center md:items-end justify-between md:justify-start gap-3 md:col-auto col-span-full">
        <div className="inline-flex flex-col items-center gap-0.5">
          <div className={`text-[30px] font-extrabold leading-none ${scoreColor}`}>
            {match.score}
          </div>
          <div
            className={`text-[11.5px] font-extrabold tracking-[0.05em] uppercase ${scoreColor}`}
          >
            {match.tier}
          </div>
        </div>
        <button
          type="button"
          disabled={match.introductionRequested || isSubmitting}
          onClick={() => onRequestIntroduction(match.partnerId, match.name)}
          className={
            match.introductionRequested
              ? "border-2 border-gray-300 text-gray-500 bg-gray-100 font-bold text-[13.5px] px-4 py-2 rounded-lg whitespace-nowrap cursor-not-allowed"
              : "border-2 border-[#F5841F] text-[#F5841F] bg-white font-bold text-[13.5px] px-4 py-2 rounded-lg whitespace-nowrap hover:bg-[#FDF1E4] disabled:opacity-60"
          }
        >
          {match.introductionRequested
            ? "Introduction Requested"
            : "Request Introduction"}
        </button>
      </div>
    </div>
  );
};

const renderStats = (statsLine: string): React.ReactNode => {
  // Bold percentage / payback fragments similar to mockup <b> tags.
  const parts = statsLine.split(/(\d[\d%–\-<>]*|\b(?:lt)?\d+\s*year\b|<1 year)/gi);
  return parts.map((part, index) => {
    if (!part) {
      return null;
    }
    const emphasize = /\d/.test(part) || /year/i.test(part);
    return emphasize ? (
      <b key={`${part}-${index}`} className="text-[#DD720F] font-bold">
        {part}
      </b>
    ) : (
      <span key={`${part}-${index}`}>{part}</span>
    );
  });
};
